import {
  addDays,
  addMinutes,
  addSeconds,
  format,
  formatDuration,
  intervalToDuration,
  isBefore,
  parse,
  startOfYesterday,
  subMinutes,
  subMonths,
} from 'date-fns';
import isEmpty from 'lodash/isEmpty';

import {
  DATE_FNS_SECONDS_FORMAT,
  DOWNLOADER_TIME_FORMAT,
  EE_BASE_URL,
  EE_TIMESTAMP_FORMAT,
  EE_TIME_FORMAT,
  PROXY_URL,
  TIMELINE_LABEL_FORMAT,
  TIME_SLOTS_CONTAINER_ID,
} from './constants';

import { primaryColor } from '../../theme';

export const parseEETimestamp = (eeTimestamp) =>
  eeTimestamp && parse(eeTimestamp, EE_TIMESTAMP_FORMAT, new Date());
export const formatEETimestamp = (date) =>
  date && format(date, EE_TIMESTAMP_FORMAT);

export const parseEESeconds = (seconds) =>
  seconds && parse(seconds, DATE_FNS_SECONDS_FORMAT, new Date());
export const formatEESeconds = (date) =>
  date && format(date, DATE_FNS_SECONDS_FORMAT);

export const getStartOfYesterday = () => formatEESeconds(startOfYesterday());
export const getPrevMonth = () => subMonths(new Date(), 1);
export const getIsMonthBeforeNow = (date) =>
  date && isBefore(date, getPrevMonth());

export const getDayStartEndTimestamp = (timestamp) => {
  const date = parseEESeconds(timestamp);
  const startTime = formatEETimestamp(date);
  const endTime = formatEETimestamp(addDays(date, 1));
  return { startTime, endTime };
};

export const getStartEndTimestamp = (timestamp, timelineHours) => {
  const date = parseEESeconds(timestamp);
  const startTime = formatEETimestamp(date);
  const endTime = formatEETimestamp(addMinutes(date, timelineHours * 60));
  return { startTime, endTime };
};

export const parseCameraOnTime = (cameraOnTime) =>
  cameraOnTime?.map(({ s, e }) => ({
    startTime: formatEESeconds(parseEETimestamp(s)),
    endTime: formatEESeconds(parseEETimestamp(e)),
  }));

const getTimezoneOffset = (timestamp) => {
  const date = parseEESeconds(timestamp);
  return date.getTimezoneOffset() * 60;
};

export const inCurrentTimezone = (timestamp) => {
  if (!timestamp) return null;
  const timezoneOffset = getTimezoneOffset(timestamp);
  return Number(timestamp) - timezoneOffset;
};
export const inUTCTimezone = (timestamp) => {
  if (!timestamp) return null;
  const timezoneOffset = getTimezoneOffset(timestamp);
  return Number(timestamp) + timezoneOffset;
};

export const formatVideoTimestamp = (timestamp) =>
  timestamp &&
  format(parseEESeconds(inCurrentTimezone(timestamp)), EE_TIME_FORMAT);

export const getTimelineNextTime = (timestamp, timelineHours) =>
  formatEESeconds(addMinutes(parseEESeconds(timestamp), timelineHours * 60));
export const getTimelinePrevTime = (timestamp, timelineHours) =>
  formatEESeconds(subMinutes(parseEESeconds(timestamp), timelineHours * 60));
export const getIsBeforeNow = (timestamp) =>
  isBefore(parseEESeconds(timestamp), new Date());

// legacy below

export const getEESession = (
  onLoadFunction,
  buildingsWithCameras,
  buildingId,
) => {
  const isPMC = !isEmpty(buildingsWithCameras);
  if (isPMC) {
    return buildingsWithCameras.forEach((buildingId) => {
      onLoadFunction(buildingId, true);
    });
  }
  if (buildingId) {
    return onLoadFunction(buildingId);
  }
  return null;
};

export const getBuildingsId = (buildings) => {
  if (!buildings) {
    return null;
  }
  let buildingsId = [];
  Object.keys(buildings).forEach((id) => {
    buildingsId = [...buildingsId, id];
  });
  return buildingsId;
};

export const getCurrentVideoTime = (startTime, videoPlaybackTime) => {
  if (!startTime || !videoPlaybackTime) return null;
  return format(
    addSeconds(parseEETimestamp(startTime), videoPlaybackTime),
    EE_TIME_FORMAT,
  );
};

export const getMinMaxMarksValue = (marks) => {
  if (!marks?.length) return {};
  const minTimelineValue = marks?.[0]?.value;
  const maxTimelineValue = marks?.[marks.length - 1]?.value;
  return { minTimelineValue, maxTimelineValue };
};

export const getOneSecondPercentWidth = (marks) => {
  const { minTimelineValue, maxTimelineValue } = getMinMaxMarksValue(marks);
  const diff = maxTimelineValue - minTimelineValue;
  return 100 / diff;
};

export const formatMarks = (marks, breakpoints) => {
  if (!marks?.length) return [];
  const { isMD, isXs } = breakpoints;
  const getDisplayedIndex = (x) => Math.floor(marks.length * (x / 4));

  if (isMD) {
    const displayedIndexes = [
      0,
      getDisplayedIndex(1),
      getDisplayedIndex(2),
      getDisplayedIndex(3),
      getDisplayedIndex(4),
      marks.length - 1,
    ];
    return marks.filter((mark, i) => displayedIndexes.includes(i));
  }
  if (isXs) {
    const displayedIndexes = [0, getDisplayedIndex(2), marks.length - 1];
    return marks.filter((mark, i) => displayedIndexes.includes(i));
  }

  return marks;
};

export function generateTimelineMarks(startTime, timelineHours) {
  if (!startTime) return [];
  let period = 1;
  switch (timelineHours) {
    case 1:
      period = 5;
      break;
    case 2:
      period = 10;
      break;
    case 4:
      period = 15;
      break;
    case 8:
      period = 20;
      break;
    default:
      period = 1;
      break;
  }
  // timeline labels should be displayed in the local time
  let timestamp = startTime;
  const timelineMarks = [
    {
      value: Number(timestamp),
      label: format(
        parseEESeconds(inCurrentTimezone(timestamp)),
        TIMELINE_LABEL_FORMAT,
      ),
    },
  ];
  do {
    timestamp = formatEESeconds(addMinutes(parseEESeconds(timestamp), period));
    timelineMarks.push({
      value: Number(timestamp),
      label: format(
        parseEESeconds(inCurrentTimezone(timestamp)),
        TIMELINE_LABEL_FORMAT,
      ),
    });
  } while (
    isBefore(
      parseEESeconds(timestamp),
      addMinutes(parseEESeconds(startTime), timelineHours * 60),
    )
  );
  return timelineMarks;
}

export function getInitialSelectValue(obj) {
  if (!obj?.id) return null;
  return {
    value: obj?.id,
    label: obj?.name,
  };
}

export const getEEBaseUrl = () => {
  if (process.env.NODE_ENV !== 'production') {
    return PROXY_URL + EE_BASE_URL;
  }
  return EE_BASE_URL;
};

export const getEEVideoListQuery = ({ esn, start, end, auth_key }) => ({
  id: esn,
  start_timestamp: start,
  end_timestamp: end,
  options: 'coalesce',
  A: auth_key,
});

export const getClipTiming = ({ s, e }) => {
  const start = parseEETimestamp(s);
  const end = parseEETimestamp(e);
  const duration = intervalToDuration({ start, end });
  const startInCurrentTz = parseEESeconds(
    inCurrentTimezone(formatEESeconds(start)),
  );
  return {
    startTime: format(startInCurrentTz, DOWNLOADER_TIME_FORMAT),
    duration: formatDuration(duration, { delimiter: ', ' }),
  };
};

export const isEEVideoSupported = () =>
  window.MediaSource &&
  window.MediaSource.isTypeSupported(
    'video/mp4; codecs="avc1.42E01E,mp4a.40.2"',
  );

// displays camera 'on' time on the timeline
export const generateCameraOnTimeIntervals = (
  timelineMarks,
  cameraOnTime,
  minTimelineValue,
) => {
  const oneSecondPercentWidth = getOneSecondPercentWidth(timelineMarks);

  const timeslotsContainerNode = document.getElementById(
    TIME_SLOTS_CONTAINER_ID,
  );

  while (timeslotsContainerNode?.firstChild) {
    timeslotsContainerNode.removeChild(timeslotsContainerNode.firstChild);
  }

  cameraOnTime.forEach(({ startTime, endTime }) => {
    const div = document.createElement('div');
    div.style.top = 0;
    div.style.position = 'absolute';
    div.style.left = `${
      (startTime - minTimelineValue) * oneSecondPercentWidth
    }%`;
    div.style.width = `${(endTime - startTime) * oneSecondPercentWidth}%`;
    div.style.height = '100%';
    div.style.backgroundColor = primaryColor;
    timeslotsContainerNode.appendChild(div);
  });
};
